/**
 * Bombs that wait (03 §2 mission 12; 10 §2 mission 34). Under `bombsHoldUntilMidnight` a charge never goes
 * off on its own fuse. With `detonateUnder` it waits for a timed event instead - the column coming down the
 * road - and goes off when one of the men that event woke walks over it. What it kills is the ranger's: he
 * laid it. A charge the column never reaches stays armed, and stays counted.
 */

import { type Ctx, isFighter } from '../ctx';
import { INTEG3, OBJECTIVES } from '../tuning';
import type { Enemy, TimedEffect } from '../types';
import { dist, findRule } from './common';

/** The men on the map because a timed event with this effect has fired. */
function wokenBy(ctx: Ctx, effect: TimedEffect): Enemy[] {
  const { world: w, statics } = ctx;
  const tags: string[] = [];
  for (const st of w.objective.timed) {
    const rule = statics.def.objective.rules[st.rule];
    if (st.fired && rule?.type === 'timedEvent' && rule.effect === effect) tags.push(rule.wakes ?? rule.effect);
  }
  if (tags.length === 0) return [];
  return w.enemies.filter((e) => isFighter(e) && e.wokenBy !== undefined && tags.includes(e.wokenBy));
}

/**
 * Is this man over the charge? Inside the radius and as close as he is going to get: on top of it, or walking
 * past it (the gap has begun to open again). A man who has only halted short of it has not reached it yet.
 * Waiting for that is what makes the blast catch the file and not just its point man.
 */
function over(e: Enemy, x: number, z: number, radius: number): boolean {
  const now = dist(e.x, e.z, x, z);
  if (now > radius) return false;
  return now <= INTEG3.detonateOnTop || now > dist(e.prevX, e.prevZ, x, z) + 1e-6;
}

/** Whichever fuse he chose, the bomb waits: for the debrief, or for the column to come over it. */
export function holdBombs(ctx: Ctx): void {
  const w = ctx.world;
  const rule = findRule(ctx.statics.def, 'bombsHoldUntilMidnight');
  if (!rule || w.bombs.length === 0) return;
  const column = rule.detonateUnder === undefined ? [] : wokenBy(ctx, rule.detonateUnder);
  const radius = rule.detonateRadius ?? INTEG3.detonateUnderRadius;
  for (const b of w.bombs) {
    if (column.some((e) => over(e, b.x, b.z, radius))) {
      // `stepProjectiles` sets it off on the next tick, as a bomb of his own: the kills are his.
      b.fuseTicks = 1;
      w.objective.chargesFired++;
    } else b.fuseTicks = OBJECTIVES.midnightFuseTicks;
  }
}
